import React, { useState } from 'react';
import { Calendar, Send, Mail, MapPin, Phone, CheckCircle2, Loader2 } from 'lucide-react';
import { contactInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fields = [
  { name: 'name', label: 'Name', type: 'text', span: 1 },
  { name: 'email', label: 'Email Address', type: 'email', span: 1 },
  { name: 'phone', label: 'Phone', type: 'tel', span: 1 },
  { name: 'website', label: 'Company Website', type: 'text', span: 1 },
  { name: 'budget', label: 'Budget', type: 'text', span: 1 },
  { name: 'location', label: 'Location', type: 'text', span: 1 },
];

const emptyForm = { name: '', email: '', phone: '', website: '', budget: '', location: '', message: '' };

const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Missing info', description: 'Please add at least your name and email.' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Something went wrong.');
      }
      setSent(true);
      setForm(emptyForm);
      toast({ title: 'Message sent!', description: 'Thanks — we’ll be in touch shortly.' });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      toast({ title: 'Could not send', description: err.message || 'Please try again or email us directly.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <p className="font-display tracking-[0.35em] text-[#c9a85f] text-sm uppercase mb-5">Get In Touch</p>
          <h2 className="font-display font-bold text-white text-4xl md:text-5xl uppercase leading-[1.05]">
            Let’s tell your story
          </h2>
          <p className="text-white/60 mt-5 leading-relaxed">
            Tell us a little about your project — or skip the form and book a discovery call.
          </p>

          <a
            href={contactInfo.calendly}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mt-8 border border-[#c9a85f]/50 hover:bg-[#c9a85f] hover:text-black text-white font-display font-semibold px-6 py-3.5 rounded-md transition-colors"
          >
            <Calendar size={18} />
            Book A Meeting
          </a>

          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3 text-white/70">
              <Mail size={18} className="text-[#c9a85f]" />
              <a href={`mailto:${contactInfo.email}`} className="text-sm hover:text-[#c9a85f] transition-colors">{contactInfo.email}</a>
            </div>
            <div className="flex items-center gap-3 text-white/70">
              <Phone size={18} className="text-[#c9a85f]" />
              <a href={`tel:${contactInfo.phoneHref}`} className="text-sm hover:text-[#c9a85f] transition-colors">{contactInfo.phone}</a>
            </div>
            <div className="flex items-center gap-3 text-white/70">
              <MapPin size={18} className="text-[#c9a85f]" />
              <span className="text-sm">{contactInfo.location}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" data-form-type="other" data-lpignore="true" className="lg:col-span-3 bg-[#111] border border-white/10 rounded-2xl p-7 md:p-9">
          <div className="grid sm:grid-cols-2 gap-5">
            {fields.map((f) => (
              <div key={f.name}>
                <label className="block text-white/70 text-xs font-display font-semibold tracking-widest uppercase mb-2">
                  {f.label}
                </label>
                <input
                  type={f.type}
                  name={f.name}
                  value={form[f.name]}
                  onChange={handleChange}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore
                  data-testid={`contact-input-${f.name}`}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-[#c9a85f] transition-colors"
                />
              </div>
            ))}
          </div>
          <div className="mt-5">
            <label className="block text-white/70 text-xs font-display font-semibold tracking-widest uppercase mb-2">Message</label>
            <textarea
              name="message"
              rows={4}
              value={form.message}
              onChange={handleChange}
              autoComplete="off"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore
              data-testid="contact-input-message"
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-[#c9a85f] transition-colors resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#c9a85f] hover:bg-[#d8b970] disabled:opacity-60 disabled:cursor-not-allowed text-black font-display font-semibold px-8 py-3.5 rounded-md transition-colors tracking-wide"
          >
            {loading ? <><Loader2 size={17} className="animate-spin" /> Sending…</> : sent ? <><CheckCircle2 size={18} /> Sent</> : <><Send size={17} /> Submit</>}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
